import { useState } from "react"
import { Link, useNavigate } from "@tanstack/react-router"
import {
  useQuizzes,
  useCreateQuiz,
  useDeleteQuiz,
  useRestoreQuiz,
  useAdminDocuments,
  useCurrentAdmin,
} from "@/lib/queries"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Plus, Pencil, Archive, ArchiveRestore, AlertTriangle } from "lucide-react"
import { hasPermission, PERMISSIONS } from "@/lib/permissions"
import { ApiError } from "@/lib/api"

const SCOPE_FILTERS = [
  { value: "active", label: "Active" },
  { value: "archived", label: "Archived" },
]

export function QuizzesListPage() {
  const navigate = useNavigate()
  const { data: currentAdmin } = useCurrentAdmin()
  const canEdit = hasPermission(currentAdmin, PERMISSIONS.MANAGE_QUIZZES)

  const [scope, setScope] = useState<string>("active")
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newQuizTitle, setNewQuizTitle] = useState("")
  const [newQuizCrId, setNewQuizCrId] = useState<string>("")

  const { data: quizzes = [], isLoading } = useQuizzes(
    scope === "archived" ? "archived" : undefined
  )
  const createQuiz = useCreateQuiz()
  const deleteQuiz = useDeleteQuiz()
  const restoreQuiz = useRestoreQuiz()

  // Fetch clinical review documents for the optional picker
  const { data: clinicalReviews = [] } = useAdminDocuments("clinical_review")

  const handleCreateQuiz = async () => {
    if (!newQuizTitle.trim()) return
    try {
      const payload: { title: string; clinical_review_document_id?: number } = {
        title: newQuizTitle,
      }
      if (newQuizCrId) {
        payload.clinical_review_document_id = parseInt(newQuizCrId)
      }
      const newQuiz = await createQuiz.mutateAsync(payload)
      setCreateDialogOpen(false)
      setNewQuizTitle("")
      setNewQuizCrId("")
      navigate({ to: "/admin/assessments/$id", params: { id: String(newQuiz.id) } })
    } catch (error) {
      console.error("Failed to create quiz:", error)
    }
  }

  const [forceTarget, setForceTarget] = useState<{
    id: number
    title: string
    organizations: string[]
  } | null>(null)

  const handleDeleteQuiz = async (id: number, title: string) => {
    try {
      await deleteQuiz.mutateAsync({ id })
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const data = error.data as { organizations?: string[] }
        setForceTarget({ id, title, organizations: data.organizations ?? [] })
      } else {
        console.error("Failed to archive quiz:", error)
      }
    }
  }

  const handleForceDeleteQuiz = async () => {
    if (!forceTarget) return
    try {
      await deleteQuiz.mutateAsync({ id: forceTarget.id, force: true })
      setForceTarget(null)
    } catch (error) {
      console.error("Failed to archive quiz:", error)
    }
  }

  const handleRestoreQuiz = async (id: number) => {
    try {
      await restoreQuiz.mutateAsync(id)
    } catch (error) {
      console.error("Failed to restore quiz:", error)
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "—"
    return new Date(dateString).toLocaleDateString("en-CA", { timeZone: "UTC" })
  }

  return (
    <div className="container mx-auto py-8 overflow-auto h-full">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Assessments</h1>
          <p className="text-muted-foreground mt-1">
            Manage assessments and their associated clinical reviews
          </p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          {canEdit && (
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                New Assessment
              </Button>
            </DialogTrigger>
          )}
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Assessment</DialogTitle>
              <DialogDescription>
                Enter a title for the new assessment. A draft version will be created
                automatically.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="quiz-title">Title</Label>
                <Input
                  id="quiz-title"
                  value={newQuizTitle}
                  onChange={(e) => setNewQuizTitle(e.target.value)}
                  placeholder="Enter assessment title"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="quiz-cr">Clinical Review (optional)</Label>
                <Select value={newQuizCrId} onValueChange={setNewQuizCrId}>
                  <SelectTrigger id="quiz-cr">
                    <SelectValue placeholder="Select a clinical review" />
                  </SelectTrigger>
                  <SelectContent>
                    {clinicalReviews.map((doc) => (
                      <SelectItem key={doc.id} value={doc.id.toString()}>
                        {doc.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateQuiz}
                disabled={!newQuizTitle.trim() || createQuiz.isPending}
              >
                {createQuiz.isPending ? "Creating..." : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList>
            {SCOPE_FILTERS.map((filter) => (
              <TabsTrigger key={filter.value} value={filter.value}>
                {filter.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">
          Loading assessments...
        </div>
      ) : quizzes.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          No assessments found.{" "}
          {scope === "active" && "Create your first assessment to get started."}
        </div>
      ) : (
        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Title</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Published Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {quizzes.map((quiz) => (
                <TableRow key={quiz.id} className="cursor-pointer">
                  <TableCell>
                    <Link
                      to="/admin/assessments/$id"
                      params={{ id: quiz.id.toString() }}
                      className="hover:underline font-medium"
                    >
                      {quiz.title}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 text-xs">
                      {quiz.has_published && (
                        <span className="text-lime-600 font-medium">
                          Published
                        </span>
                      )}
                      {quiz.has_draft && (
                        <span className="text-orange-500 font-medium">
                          Draft
                        </span>
                      )}
                      {!quiz.has_published && !quiz.has_draft && (
                        <span className="text-muted-foreground">No Version</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(quiz.published_at)}</TableCell>
                  <TableCell className="text-right">
                    {scope === "archived" ? (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => e.stopPropagation()}
                            title="Restore"
                          >
                            <ArchiveRestore className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent
                          onClick={(e) => e.stopPropagation()}
                        >
                          <AlertDialogHeader>
                            <AlertDialogTitle>Restore Assessment</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to restore &quot;{quiz.title}&quot;?
                              It will be moved back to the active assessments list.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleRestoreQuiz(quiz.id)}
                            >
                              Restore
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    ) : (
                      <>
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation()
                              navigate({ to: "/admin/assessments/$id", params: { id: quiz.id.toString() } })
                            }}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canEdit && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={(e) => e.stopPropagation()}
                                title="Archive"
                              >
                                <Archive className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent
                              onClick={(e) => e.stopPropagation()}
                            >
                              <AlertDialogHeader>
                                <AlertDialogTitle>Archive Assessment</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to archive &quot;{quiz.title}&quot;?
                                  It will be moved to the Archived tab.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteQuiz(quiz.id, quiz.title)}
                                  disabled={deleteQuiz.isPending}
                                >
                                  {deleteQuiz.isPending ? "Archiving..." : "Archive"}
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Shown when archiving hit a 409: staff still working through the quiz */}
      <AlertDialog
        open={forceTarget !== null}
        onOpenChange={(open) => { if (!open) setForceTarget(null) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assessment Has Incomplete Assignments</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 mb-3 dark:border-amber-900 dark:bg-amber-950">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <span className="text-amber-800 dark:text-amber-200">
                    &quot;{forceTarget?.title}&quot; is assigned to staff who have
                    not completed it. Archiving will hide it from their training
                    list.
                  </span>
                </div>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {forceTarget?.organizations.map((org) => (
                    <li key={org}>{org}</li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleForceDeleteQuiz}
              disabled={deleteQuiz.isPending}
            >
              {deleteQuiz.isPending ? "Archiving..." : "Archive Anyway"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
